import React, { useMemo } from 'react';
import clsx from 'clsx';
import { SectionSummaryHSPProps } from './SectionSummaryHSP.types';
import { SVGIcon } from '../SVGIcon/SVGIcon';
import { IconColor, IconName } from '../SVGIcon/SVGIcon.types';

/**
 * Komponent SectionSummaryHSP wyświetla sekcję ostrzeżeń i rekomendacji HSP z różnymi wariantami kolorystycznymi
 *
 * @example
 * ```tsx
 * <SectionSummaryHSP
 *   variant="danger"
 *   iconName="warning"
 *   title="Szkoda powyżej 40% wartości"
 *   content="Pojazd uczestniczył w poważnej kolizji..."
 *   recommendation="Sprawdź jakość wykonanej naprawy"
 * />
 * ```
 */
export const SectionSummaryHSP: React.FC<SectionSummaryHSPProps> = ({
  variant,
  title,
  content,
  recommendation,
  className,
  i18n,
}) => {
  const recommendationLabel = i18n?.recommendationLabel || 'Rekomendacja:';
  // Definiowanie kolorów dla poszczególnych wariantów - memoized jako stałe
  const variantStyles = useMemo(
    () => ({
      danger: {
        border: 'border-legalStatus-dangerBorder',
        iconBg: 'bg-red-200',
        icon: 'error-circle',
        iconFill: '',
        titleColor: 'text-red-800',
        contentColor: 'text-legalStatus-dangerText',
        recommendationBg: 'bg-red-800',
        recommendationText: 'text-white',
      },
      warning: {
        border: 'border-yellowDark',
        iconBg: 'bg-yellow',
        icon: 'warning-triangle',
        iconFill: 'warning-iconColor',
        titleColor: 'text-warning-titleColor',
        contentColor: 'text-warning-contentColor',
        recommendationBg: 'bg-yellow',
        recommendationText: 'text-warning-contentColor',
      },
      success: {
        border: 'border-legalStatus-successBorder',
        iconBg: 'bg-legalStatus-successBorder',
        icon: 'check-circle',
        iconFill: '',
        titleColor: 'text-green-700',
        contentColor: 'text-grayText',
        recommendationBg: '',
        recommendationText: '',
      },
    }),
    [],
  );

  // Aktualnie używane style - memoized bazując na variant
  const styles = useMemo(
    () => variantStyles[variant],
    [variantStyles, variant],
  );

  // Container classes - memoized based on styles and className
  const containerClasses = useMemo(
    () =>
      clsx(
        'overflow-hidden rounded-[15px] border bg-white',
        styles.border,
        className,
      ),
    [styles.border, className],
  );

  // Icon container classes - memoized based on styles
  const iconContainerClasses = useMemo(
    () =>
      clsx(
        'flex h-10 w-10 items-center justify-center rounded-[10px]',
        styles.iconBg,
      ),
    [styles.iconBg],
  );

  // Title classes - memoized based on styles
  const titleClasses = useMemo(
    () =>
      clsx(
        'font-inter text-[20px] lg:text-[22px] font-bold leading-[1.27]',
        styles.titleColor,
      ),
    [styles.titleColor],
  );

  // Content classes - memoized based on styles
  const contentClasses = useMemo(
    () => clsx('font-inter text-sm leading-[1.57]', styles.contentColor),
    [styles.contentColor],
  );

  // Recommendation container classes - memoized based on styles
  const recommendationContainerClasses = useMemo(
    () => clsx('mt-5 px-5 py-4', styles.recommendationBg),
    [styles.recommendationBg],
  );

  // Recommendation text classes - memoized based on styles
  const recommendationTextClasses = useMemo(
    () => clsx('font-inter text-sm leading-[1.57]', styles.recommendationText),
    [styles.recommendationText],
  );

  return (
    <div className={containerClasses}>
      {/* Główna sekcja z ikoną, tytułem i treścią */}
      <div className="px-5 pb-0 pt-7">
        <div className="mb-[18px] flex items-center gap-6">
          <div
            className={clsx('flex-shrink-0 flex-grow-0', iconContainerClasses)}
          >
            <SVGIcon
              name={styles.icon as IconName}
              width={20}
              height={20}
              fill={styles.iconFill as IconColor}
            />
          </div>
          <h3 className={titleClasses}>{title}</h3>
        </div>
        <p className={contentClasses}>{content}</p>
      </div>

      {/* Sekcja rekomendacji - tylko dla danger i warning */}
      {recommendation && variant !== 'success' && (
        <div className={recommendationContainerClasses}>
          <p className={recommendationTextClasses}>
            <strong className="font-extrabold">{recommendationLabel}</strong>{' '}
            {recommendation}
          </p>
        </div>
      )}

      {/* Padding na dole dla success bez rekomendacji */}
      {variant === 'success' && <div className="pb-7" />}
    </div>
  );
};
